<!-- Override Indicators (CR-20260722-002). Layout mirrors the owner's sketch: centred date label,
     then one row of three boxes flanked by the range arrows.
     TWO manual-override cues, deliberately at different scopes (D21):
       frame + legend  -> `range-overridden`: SOME date in this tab's range is overridden;
       label + values  -> `overridden`:       THIS date is.
     BOTH change types speak YELLOW (owner screencast ruling 2026-08-31 evening — "magenta goes
     away", reversing the magenta placeholder this file used to describe). They are told apart by
     WHERE the yellow lands, not by hue: border + text = manually overridden, background + black
     font = changed since the last run. See the SCSS, which is the authority on the colour. -->
<fieldset class="fieldset override-indicators" [class.range-overridden]="this.rangeOverridden"
          data-element="override-indicators">
  <legend class="fieldset-title">{{ this.legend }}</legend>

  <!-- The source hint rides on the SAME line as the label so stepping on/off the "All dates"
       position never adds or removes a row — a reflowing date row shifts the three boxes under
       the user's cursor, the same flicker the box spinner is positioned absolutely to avoid. -->
  <div class="date-row">
    <span class="date-label" [class.overridden]="this.overridden"
          data-element="override-date-label">{{ this.dateLabel || '—' }}</span>
    <span class="date-source" *ngIf="this.sourceDateLabel"
          data-element="override-date-source"> · Values From {{ this.sourceDateLabel }}</span>
  </div>

  <!-- CR-20260831-005: focus on any box freezes ALL THREE displayed values (this fieldset only)
       so live ticks cannot repaint under the cursor; leaving the row unfreezes and the boxes
       catch up to the live reading — unless an edit committed, which promoted an override. -->
  <div class="values-row" [class.overridden]="this.overridden"
       (focusin)="this.onValuesFocusIn()" (focusout)="this.onValuesFocusOut($event)">
    <!-- Arrows occupy their slot even when hidden so the three boxes stay put between modes. -->
    <span class="arrow" *ngIf="this.showArrows" [class.arrow-off]="!this.canPrev || this.effectiveDisabled"
          data-element="override-prev-date" (click)="this.onPrev()">&#8592;</span>

    <span class="key">IV</span>
    <!-- CR-20260723-002: dblclick the wrapper (it catches the event even while the box is disabled by
         its spinner) to take manual control of a loading date — see onSpinningTakeover. -->
    <span class="box-wrap" (dblclick)="this.onSpinningTakeover()"
          [title]="this.ivLoading ? 'Double-Click to Override While Loading' : null">
      <!-- [min]=0 makes the CONTROL refuse a negative (DevExtreme clamps on commit) while leaving
           zero visibly rejected by box-invalid rather than silently rewritten to some epsilon. -->
      <!-- CR-20260831-004: .param-changed (yellow BACKGROUND) = this value differs from what the
           tab's last run sent. Independent of the yellow manual-override treatment (border/text)
           — the two coexist on one box. -->
      <dx-number-box class="box" data-element="override-iv"
                     [class.param-changed]="this.ivChanged"
                     [class.box-invalid]="this.ivInvalid"
                     [value]="this.displayIv"
                     [disabled]="this.disabled || this.ivLoading"
                     [readOnly]="this.superUserReadOnly"
                     [showSpinButtons]="false"
                     [min]="0"
                     [step]="0.001"
                     format="#0.0%"
                     (onValueChanged)="this.onIvChanged($event)"></dx-number-box>
      <dx-load-indicator class="box-spinner" *ngIf="this.ivLoading"
                         [height]="14" [width]="14"></dx-load-indicator>
    </span>

    <span class="key">HS</span>
    <dx-number-box class="box" data-element="override-hs"
                   [class.param-changed]="this.hsChanged"
                   [class.box-invalid]="this.hsInvalid"
                   [value]="this.displayHs"
                   [disabled]="this.disabled"
                   [readOnly]="this.superUserReadOnly"
                   [showSpinButtons]="false"
                   [min]="0"
                   [step]="0.01"
                   format="#0.00"
                   (onValueChanged)="this.onHsChanged($event)"></dx-number-box>

    <span class="key">VS</span>
    <!-- CR-20260723-002: dblclick to take manual control while the vertical-skew slot is warming. -->
    <span class="box-wrap" (dblclick)="this.onSpinningTakeover()"
          [title]="this.vsLoading ? 'Double-Click to Override While Loading' : null">
      <dx-number-box class="box" data-element="override-vs"
                     [class.param-changed]="this.vsChanged"
                     [class.box-invalid]="this.vsInvalid"
                     [value]="this.displayVs"
                     [disabled]="this.disabled || this.vsLoading"
                     [readOnly]="this.superUserReadOnly"
                     [showSpinButtons]="false"
                     [min]="0"
                     format="#0"
                     (onValueChanged)="this.onVsChanged($event)"></dx-number-box>
      <!-- Vertical skew can take seconds when nobody is watching the expiration (the backend warms
           a slot), so this spinner is the one that actually matters to the user. -->
      <dx-load-indicator class="box-spinner" *ngIf="this.vsLoading"
                         [height]="14" [width]="14"></dx-load-indicator>
    </span>

    <span class="arrow" *ngIf="this.showArrows" [class.arrow-off]="!this.canNext || this.effectiveDisabled"
          data-element="override-next-date" (click)="this.onNext()">&#8594;</span>
  </div>

  <!-- Clears THIS date only (owner ruling D10) — the boxes re-seed with live readings. -->
  <div class="clear-row" *ngIf="this.overridden">
    <span class="clear-link" [class.clear-off]="!this.canClear"
          data-element="override-clear" (click)="this.onClear()">Clear Overrides</span>
  </div>
</fieldset>
